import type { RadioOption } from '../../../components/inputs/RadioGroup';
import type { CorrespondentKind, PersonNameParts } from './routingTypes';

/*
  What both register forms decide the same way, kept out of the component files so each of those
  exports components alone — a file exporting a component and a function loses fast refresh.
*/

export const EMPTY_NAME: PersonNameParts = {
  salutation: '',
  firstName: '',
  middleName: '',
  lastName: '',
  suffix: '',
};

/**
 * Whether any of the four NAME parts holds anything.
 *
 * **The salutation is deliberately not one of them**, and this is the frontend half of the same
 * call the server's `RoutingPersonRules.AnyPart` makes. This predicate decides whether a
 * correspondent is NAMED at all — whether an identity row gets written. A title with no name
 * behind it is not a person, so a lone "Mr." must not summon one; it would create a row the
 * half-written-name rule then refuses. A salutation rides along with a name, never summons one.
 *
 * Contrast `ChangesIdentity` on the personnel update, which asks the opposite question — whether
 * an EXISTING identity is being edited — and therefore DOES count it.
 */
export function anyNamePart(name: PersonNameParts): boolean {
  return [name.firstName, name.middleName, name.lastName, name.suffix].some((part) => part.trim().length > 0);
}

/** A name with some parts and not both halves — refused by the server, so said by the form first. */
export function nameIncomplete(name: PersonNameParts): boolean {
  return anyNamePart(name) && (name.firstName.trim().length === 0 || name.lastName.trim().length === 0);
}

/** PDF, PNG or JPEG at 10 MB — the `user-documents` allowlist and cap the endpoint enforces. */
export const ROUTING_FILE_MAX_BYTES = 10 * 1024 * 1024;

/** Whether a picked file would be refused at the door for its size. */
export function fileTooLarge(file: File): boolean {
  return file.size > ROUTING_FILE_MAX_BYTES;
}

/**
 * The two places a correspondent sits — a unit picked from the org chart, or an organization
 * typed in. Only the outside one can carry a named person: a unit's people are personnel, not
 * correspondents.
 */
export const CORRESPONDENT_KIND_OPTIONS: RadioOption<CorrespondentKind>[] = [
  { value: 'internal', label: 'Within the organization', description: 'A unit of this organization, picked from the org chart.' },
  { value: 'external', label: 'External', description: 'An office, agency or company outside it, typed in — with the person, if there is one.' },
];
